import { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
    Heart, 
    Building2, 
    PartyPopper, 
    Music4,
    ArrowRight,
    Sparkles
} from 'lucide-react';

const services = [
    {
        icon: Heart,
        title: 'Weddings',
        description: 'Make your special day unforgettable with soulful melodies and energetic performances that keep your guests dancing all night.',
        image: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/ae7t0urh_13.jpeg',
        features: ['Sangeet Night', 'Mehendi Ceremony', 'Wedding Reception', 'Cocktail Party'],
        color: 'from-pink-500/20 to-rose-500/20',
        accentColor: 'pink',
    },
    {
        icon: Building2,
        title: 'Corporate Events',
        description: 'Elevate your corporate gatherings with professional live entertainment that impresses clients and motivates teams.',
        image: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/rz7ko0bw_14.jpeg',
        features: ['Annual Days', 'Product Launches', 'Award Ceremonies', 'Team Building'],
        color: 'from-blue-500/20 to-cyan-500/20',
        accentColor: 'blue',
    },
    {
        icon: PartyPopper,
        title: 'Private Parties',
        description: 'From birthdays to anniversaries, we bring the perfect mix of energy and elegance to your intimate celebrations.',
        image: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/0tqq5lae_7.jpeg',
        features: ['Birthday Parties', 'Anniversaries', 'House Parties', 'New Year Eve'],
        color: 'from-purple-500/20 to-violet-500/20',
        accentColor: 'purple',
    },
    {
        icon: Music4,
        title: 'Concerts',
        description: 'Experience the raw energy of live concerts. Perfect for music festivals, college fests, and large-scale events.',
        image: 'https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/zrs4fm4l_6.jpeg',
        features: ['Music Festivals', 'College Fests', 'Club Nights', 'Open Air Shows'],
        color: 'from-amber-500/20 to-orange-500/20',
        accentColor: 'amber',
    },
];

// 3D Tilt Card
const TiltCard = ({ children, className = '' }) => {
    const ref = useRef(null);

    const handleMouseMove = (e) => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const rotateX = (y - centerY) / 25;
        const rotateY = (centerX - x) / 25;
        ref.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
    };

    const handleMouseLeave = () => {
        if (!ref.current) return;
        ref.current.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
    };

    return (
        <div
            ref={ref}
            className={`transition-transform duration-300 ease-out ${className}`}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ transformStyle: 'preserve-3d' }}
        >
            {children}
        </div>
    );
};

export const ServicesSection = () => {
    const containerRef = useRef(null);
    const isInView = useInView(containerRef, { once: true, margin: "-100px" });
    
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ['start end', 'end start'],
    });

    const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);

    return (
        <section ref={containerRef} className="py-24 lg:py-32 relative overflow-hidden">
            {/* Animated Background */}
            <motion.div 
                className="absolute inset-0 pointer-events-none"
                style={{ y: backgroundY }}
            >
                <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-background to-transparent" />
                <div className="absolute inset-0 spotlight opacity-30" />
                <motion.div
                    className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[150px]"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.05, 0.1, 0.05],
                    }}
                    transition={{ duration: 8, repeat: Infinity }}
                />
            </motion.div>
            
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.7 }}
                    className="text-center mb-16 lg:mb-20"
                >
                    <motion.div 
                        className="inline-flex items-center gap-2 mb-4"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={isInView ? { opacity: 1, scale: 1 } : {}}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        <Sparkles className="w-5 h-5 text-primary" />
                        <span className="text-sm font-body font-medium text-primary uppercase tracking-wider">
                            What We Offer
                        </span>
                    </motion.div>
                    <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-5">
                        Perfect for Every Occasion
                    </h2>
                    <p className="text-muted-foreground text-base sm:text-lg font-body max-w-2xl mx-auto">
                        From intimate gatherings to grand celebrations, we craft the perfect musical atmosphere for your event
                    </p>
                </motion.div>

                {/* Services Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
                    {services.map((service, index) => (
                        <motion.div
                            key={service.title}
                            initial={{ opacity: 0, y: 40 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.6, delay: index * 0.15 }}
                        >
                            <TiltCard>
                                <Card className="card-premium rounded-3xl overflow-hidden h-full group">
                                    <CardContent className="p-0 flex flex-col h-full">
                                        {/* Image Section */}
                                        <div className="relative h-56 sm:h-64 overflow-hidden">
                                            <motion.img
                                                src={service.image}
                                                alt={service.title}
                                                className="w-full h-full object-cover"
                                                whileHover={{ scale: 1.1 }}
                                                transition={{ duration: 0.6 }}
                                            />
                                            {/* Gradient Overlay */}
                                            <div className={`absolute inset-0 bg-gradient-to-t ${service.color} to-transparent opacity-60`} />
                                            <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent" />
                                            
                                            {/* Icon Badge */}
                                            <motion.div 
                                                className="absolute top-5 left-5"
                                                whileHover={{ scale: 1.1, rotate: 5 }}
                                            >
                                                <div className="w-14 h-14 rounded-2xl bg-card/80 backdrop-blur-md border border-border/50 flex items-center justify-center shadow-lg">
                                                    <service.icon className="w-7 h-7 text-primary" />
                                                </div>
                                            </motion.div>
                                        </div>

                                        {/* Content Section */}
                                        <div className="p-7 flex flex-col flex-1">
                                            <h3 className="font-heading font-bold text-2xl text-foreground mb-3">
                                                {service.title}
                                            </h3>
                                            <p className="text-muted-foreground text-sm font-body mb-5 leading-relaxed">
                                                {service.description}
                                            </p>

                                            {/* Features Tags */}
                                            <div className="flex flex-wrap gap-2 mb-6">
                                                {service.features.map((feature, i) => (
                                                    <motion.span
                                                        key={feature}
                                                        initial={{ opacity: 0, scale: 0.8 }}
                                                        animate={isInView ? { opacity: 1, scale: 1 } : {}}
                                                        transition={{ delay: index * 0.15 + i * 0.05 + 0.3 }}
                                                        className="px-3 py-1.5 text-xs font-body bg-muted/50 text-muted-foreground rounded-full border border-border/30 hover:border-primary/40 hover:text-primary transition-colors"
                                                    >
                                                        {feature}
                                                    </motion.span>
                                                ))}
                                            </div>

                                            {/* CTA - Push to bottom */}
                                            <div className="mt-auto">
                                                <Link to="/book">
                                                    <Button 
                                                        variant="ghost" 
                                                        className="p-0 h-auto text-primary hover:text-primary/80 font-heading font-semibold group/btn"
                                                    >
                                                        Book for {service.title}
                                                        <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover/btn:translate-x-2" />
                                                    </Button>
                                                </Link>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </TiltCard>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
